import {
  bindEmail as bindEmailRequest,
  bindPhone as bindPhoneRequest,
  changeFundPassword as changeFundPasswordRequest,
  changePassword,
  listLoginDevices,
  listSecurityLogs,
  resetFundPassword as resetFundPasswordRequest,
  revokeLoginDevice,
  sendForgotFundPasswordCode,
  sendSecurityEmailCode,
  sendSecurityPhoneCode,
  setFundPassword as setFundPasswordRequest,
} from '~/api/security'
import { mapLoginDevices, mapSecurityLog } from '~/pages/me/security/utils/map'
import type { FundMode, SecDevice, SecLog, SecSheet } from '~/pages/me/security/types'
import type { EmailBindRequestDto, PhoneBindRequestDto } from '~/types/api/security'
import { FUND_SET_SESSION_KEY } from '~/utils/fundPasswordSession'

const maskPhone = (raw: string) => {
  if (raw.length < 7) return raw
  return `${raw.slice(0, 3)}****${raw.slice(-4)}`
}

const maskEmail = (raw: string) => {
  const at = raw.indexOf('@')
  if (at < 1) return raw
  const local = raw.slice(0, at)
  const domain = raw.slice(at)
  const head = local.slice(0, Math.min(2, local.length))
  return `${head}***${domain}`
}

/** refresh 递增，丢弃进行中的 loadMore 结果 */
let logsGen = 0

/** 安全中心状态（设备 / 资金密码 / 换绑） */
export const useSecurity = () => {
  const { t } = useI18n()
  const userStore = useUserStore()

  const phone = computed(() => userStore.user.phone)
  const email = computed(() => userStore.user.email)

  /**
   * 是否已设置资金密码。
   * useState 作会话权威源：设置成功立即变「修改」，并与 store /me 回填对齐。
   */
  const fundSetFlag = useState<boolean>(FUND_SET_SESSION_KEY, () => false)
  if (userStore.user.hasFundPassword && !fundSetFlag.value) {
    fundSetFlag.value = true
  }
  const fundSet = computed(() => fundSetFlag.value || userStore.user.hasFundPassword)

  const devices = useState<SecDevice[]>('sec-devices', () => [])
  const devicesLoading = useState('sec-devices-loading', () => false)
  const kickingId = useState<string | null>('sec-devices-kicking', () => null)
  const logs = useState<SecLog[]>('sec-logs', () => [])
  const logsLoading = useState('sec-logs-loading', () => false)
  const logsLoadingMore = useState('sec-logs-loading-more', () => false)
  const logsPage = useState('sec-logs-page', () => 0)
  const logsHasMore = useState('sec-logs-has-more', () => false)

  const sheet = useState<SecSheet>('sec-sheet', () => null)
  const fundMode = useState<FundMode>('sec-fund-mode', () => 'setup')

  const phoneMasked = computed(() => (phone.value ? maskPhone(phone.value) : ''))
  const emailMasked = computed(() => (email.value ? maskEmail(email.value) : ''))

  const openSheet = (s: Exclude<SecSheet, null>) => {
    if (s === 'fund') fundMode.value = fundSet.value ? 'change' : 'setup'
    sheet.value = s
  }

  const closeSheet = () => {
    sheet.value = null
  }

  const refreshLogsQuietly = () => {
    void refreshLogs().catch(() => {
      /* 列表刷新失败不影响操作成功态 */
    })
  }

  const markFundPasswordSet = () => {
    fundSetFlag.value = true
    userStore.setProfile({ hasFundPassword: true })
    refreshLogsQuietly()
  }

  const refreshDevices = async () => {
    devicesLoading.value = true
    try {
      const res = await listLoginDevices()
      devices.value = mapLoginDevices(res.items ?? [], t)
    } finally {
      devicesLoading.value = false
    }
  }

  /** reset=true 拉第 1 页，否则追加下一页 */
  const fetchLogs = async (reset: boolean) => {
    if (!reset && (logsLoading.value || logsLoadingMore.value || !logsHasMore.value)) return
    const flag = reset ? logsLoading : logsLoadingMore
    flag.value = true
    const gen = reset ? ++logsGen : logsGen
    const page = reset ? 1 : logsPage.value + 1
    try {
      const res = await listSecurityLogs({ page })
      if (gen !== logsGen) return
      const incoming = (res.items ?? []).map(item => mapSecurityLog(item, t))
      let nextLogs = incoming
      if (!reset) {
        const seen = new Set(logs.value.map(row => row.id))
        nextLogs = logs.value.concat(incoming.filter(item => !seen.has(item.id)))
      }
      logs.value = nextLogs
      logsPage.value = res.page ?? page
      logsHasMore.value = incoming.length > 0 && nextLogs.length < (res.total ?? 0)
    } finally {
      if (!reset || gen === logsGen) flag.value = false
    }
  }

  const refreshLogs = () => fetchLogs(true)
  const loadMoreLogs = () => fetchLogs(false)

  /** 改登录密码：校验旧密码后更新，成功后踢非本机设备 */
  const changeLoginPwd = async (oldPwd: string, newPwd: string) => {
    await changePassword({
      old_password: oldPwd,
      new_password: newPwd,
      confirm_password: newPwd,
    })
    devices.value = devices.value.filter(d => d.current)
    refreshLogsQuietly()
    void refreshDevices().catch(() => {
      /* 列表刷新失败不影响改密成功态 */
    })
  }

  const setFundPassword = async (pwd: string) => {
    await setFundPasswordRequest({
      new_fund_password: pwd,
      confirm_fund_password: pwd,
    })
    markFundPasswordSet()
  }

  const changeFundPassword = async (oldPwd: string, newPwd: string) => {
    await changeFundPasswordRequest({
      current_fund_password: oldPwd,
      new_fund_password: newPwd,
      confirm_fund_password: newPwd,
    })
    markFundPasswordSet()
  }

  const resetFundPassword = async (type: 'phone' | 'email', code: string, newPwd: string) => {
    await resetFundPasswordRequest({
      type,
      code,
      new_fund_password: newPwd,
      confirm_fund_password: newPwd,
    })
    markFundPasswordSet()
  }

  const bindPhone = async (payload: PhoneBindRequestDto) => {
    await bindPhoneRequest(payload)
    userStore.setProfile({ phone: payload.new_phone })
    refreshLogsQuietly()
  }

  const bindEmail = async (payload: EmailBindRequestDto) => {
    await bindEmailRequest(payload)
    userStore.setProfile({ email: payload.new_email })
    refreshLogsQuietly()
  }

  const kickDevice = async (id: string) => {
    if (kickingId.value) return false
    const target = devices.value.find(d => d.id === id)
    if (!target || target.current) return false
    kickingId.value = id
    try {
      await revokeLoginDevice(id)
      devices.value = devices.value.filter(d => d.id !== id)
      refreshLogsQuietly()
      return true
    } finally {
      kickingId.value = null
    }
  }

  return {
    phone,
    email,
    devices,
    devicesLoading,
    kickingId,
    logs,
    logsLoading,
    logsLoadingMore,
    logsHasMore,
    sheet,
    fundMode,
    phoneMasked,
    emailMasked,
    fundSet,
    openSheet,
    closeSheet,
    refreshDevices,
    refreshLogs,
    loadMoreLogs,
    changeLoginPwd,
    setFundPassword,
    changeFundPassword,
    sendFundResetCode: (type: 'phone' | 'email') => sendForgotFundPasswordCode({ type }),
    resetFundPassword,
    sendPhoneCode: sendSecurityPhoneCode,
    bindPhone,
    sendEmailCode: sendSecurityEmailCode,
    bindEmail,
    kickDevice,
  }
}
